import { useFormik } from "formik";
import { Button, Empty, Select } from "antd";
import { PlusOutlined, ToolOutlined } from "@ant-design/icons";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useToast } from "../../../../contexts/ToastContext";
import Downicon from "../../../../assets/icons/bx-download.png";
import apiClient from "../../../../helpers/apiClient";
import uploadFile from "../../../../utilities/uploadFile";
import CarDetailSection from "../../../cars/components/cardetails/CarDetailSection";
import displayValue from "../../../../utilities/displayValue";

const SERVICE_TYPE_VALUES = [
    "Tires",
    "Wipers",
    "Oil",
    "Break Pads",
    "Rotors",
    "Headlights",
    "Filters",
    "Cabin filter",
    "Other",
];

const SERVICE_TYPE_I18N = {
    Tires: "tires",
    Wipers: "wipers",
    Oil: "oil",
    "Break Pads": "breakPads",
    Rotors: "rotors",
    Headlights: "headlights",
    Filters: "filters",
    "Cabin filter": "cabinFilter",
    Other: "other",
};

const primaryBtnClass = "border-[#5d5dff] bg-[#5d5dff] hover:!border-[#4b4be6] hover:!bg-[#4b4be6]";
function MaintenanceCard({ data, t, na, showType }) {
    const formattedDate = data.date ? new Date(data.date).toLocaleDateString() : na;

    return (
        <div className="mb-3 rounded-lg border border-[#f0f0f0] bg-white p-4">
            {showType && (
                <div className="fontSemi mb-2 text-base">{displayValue(data.serviceType, na)}</div>
            )}
            <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                    {t("carDetail.vendor")}: <span className="textBlue">{displayValue(data.vendor, na)}</span>
                </div>
                <div>
                    {t("carDetail.price")}: <span className="textBlue">{data.price ? `$ ${data.price}` : na}</span>
                </div>
                <div>
                    {t("carDetail.odometer")}: <span className="textBlue">{displayValue(data.odometer, na)}</span>
                </div>
                <div>
                    {t("carDetail.date")}: <span className="textBlue">{formattedDate}</span>
                </div>
                <div className="col-span-2">
                    {t("carDetail.comment")}: <span className="textBlue">{displayValue(data.comment, na)}</span>
                </div>
                <div>
                    {data.doc ? (
                        <a href={data.doc}>
                            <span className="textBlue fontRegular underline">{t("carDetail.downloadPdf")}</span>{" "}
                            <img src={Downicon} className="h-[0.8rem]" alt="DL" />
                        </a>
                    ) : (
                        na
                    )}
                </div>
            </div>
        </div>
    );
}

MaintenanceCard.propTypes = {
    data: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    na: PropTypes.string.isRequired,
    showType: PropTypes.bool,
};

function MaintenCar(props) {
    const toast = useToast();
    const { t } = useTranslation("cars");
    const na = t("notAvailable");

    const [, setError] = useState();
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [status, setStatus] = useState(props.status);

    const [serviceType, setServiceType] = useState("");
    const [selectServiceType, setSelectServiceType] = useState("");
    const [edit, setEdit] = useState(false);
    const [edit1, setEdit1] = useState(false);
    const [maintainData, setMaintainData] = useState();
    const [serviceFile, setServiceFile] = useState(null);
    const [addServiceError, setAddServiceError] = useState("");
    const [addServiceLoading, setAddServiceLoading] = useState(false);

    const fetchMaintainData = useCallback(async () => {
        try {
            setFetchLoading(true);
            const res = await apiClient.get(`/api/car/${props?.data}`);
            const maintanance = res?.data?.data?.maintenance || [];
            setMaintainData(maintanance);
        } catch (_err) {
            setMaintainData([]);
        } finally {
            setFetchLoading(false);
        }
    }, [props?.data]);

    useEffect(() => {
        fetchMaintainData();
    }, [fetchMaintainData]);

    const addServiceForm = useFormik({
        initialValues: {
            company: "",
            comment: "",
            date: "",
            doc: "",
            odometer: "",
            price: "",
            vendor: "",
            serviceType: "",
        },
        onSubmit: async (values) => {
            if (!serviceType) {
                setAddServiceError(t("carDetail.serviceTypeRequired"));
                return;
            }
            if (!values.vendor) {
                setAddServiceError(t("carDetail.vendorRequired"));
                return;
            }
            if (!values.date) {
                setAddServiceError(t("carDetail.dateRequired"));
                return;
            }
            if (!values.price) {
                setAddServiceError(t("carDetail.priceRequired"));
                return;
            }
            if (!values.odometer) {
                setAddServiceError(t("carDetail.odometerRequired"));
                return;
            }
            if (!serviceFile) {
                setAddServiceError(t("carDetail.documentRequired"));
                return;
            }
            setAddServiceError("");
            const updatedValues = { ...values, serviceType };
            try {
                setAddServiceLoading(true);
                const uploadedFile = await uploadFile(updatedValues.doc);
                const payload = {
                    maintenance: [...maintainData, { ...updatedValues, doc: uploadedFile }],
                };
                const res = await apiClient.patch(`/api/car/${props?.id}`, payload);
                if (res?.data?.success) {
                    setAddServiceError("");
                    toast.success({
                        message: t("success"),
                        description: t("carDetail.maintenanceInfoAdded"),
                    });
                    setEdit(false);
                    fetchMaintainData();
                    addServiceForm.resetForm();
                }

                setAddServiceLoading(false);
            } catch (err) {
                setAddServiceLoading(false);
                // console.error("error creating maintanance", err?.response?.data || err);
                setAddServiceError(err?.response?.data?.message || t("carDetail.errorUploadingMaintenance"));
            }
        },
    });

    const turnOn = async () => {
        setEdit1(false);
        setLoading(true);

        try {
            const res = await apiClient.patch(`/api/car/${props.id}`, {
                maintanenceMode: {
                    status: true,
                    startTime: new Date(),
                },
            });

            if (res.status === 200) {
                toast.success({
                    message: t("success"),
                    description: t("carDetail.maintenanceModeEnabled"),
                });
                setEdit1(true);
                setEdit(true);
                setLoading(false);
            }
        } catch (err) {
            setError(err);
            setLoading(false);
        }
    };

    const trunOff = async () => {
        setLoading(true);
        setStatus(false);
        try {
            const res = await apiClient.patch(`/api/car/${props.id}`, {
                maintanenceMode: {
                    status: false,
                    startTime: new Date(),
                },
            });

            if (res.status === 200) {
                toast.success({
                    message: t("success"),
                    description: t("carDetail.maintenanceModeDisabled"),
                });
                setEdit1(true);
                setEdit(true);
                setLoading(false);
            }
        } catch (err) {
            setError(err);
            setLoading(false);
        }
    };

    const serviceFileChange = (e) => {
        const [file] = e.target.files;
        if (file && file.type === "application/pdf") {
            setServiceFile(file);
            addServiceForm.values.doc = file;
        }
    };

    const filteredItems = (maintainData || [])
        .filter((data) => selectServiceType === "" || data.serviceType === selectServiceType)
        .slice()
        .reverse();

    const serviceTypeOptions = useMemo(
        () => [
            { value: "", label: t("carDetail.allServices") },
            ...SERVICE_TYPE_VALUES.map((value) => ({
                value,
                label: t(`carDetail.${SERVICE_TYPE_I18N[value]}`),
            })),
        ],
        [t],
    );

    const headerAction = (
        <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
            <Select
                value={selectServiceType}
                onChange={setSelectServiceType}
                options={serviceTypeOptions}
                className="min-w-[11rem]"
                size="middle"
            />
            {edit ? (
                <>
                    <Button
                        shape="round"
                        onClick={() => {
                            addServiceForm.resetForm();
                            setEdit(false);
                        }}>
                        {t("rates.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        shape="round"
                        className={primaryBtnClass}
                        loading={addServiceLoading}
                        onClick={addServiceForm.handleSubmit}>
                        {t("carDetail.saveService")}
                    </Button>
                </>
            ) : (
                <>
                    <Button
                        type="primary"
                        shape="round"
                        icon={<PlusOutlined />}
                        className={primaryBtnClass}
                        onClick={() => {
                            setEdit(true);
                            setEdit1(false);
                        }}>
                        {t("carDetail.recordMaintenanceDetails")}
                    </Button>
                    <Button
                        shape="round"
                        icon={<ToolOutlined />}
                        type={status ? "primary" : "default"}
                        danger={status}
                        className={status ? undefined : "border-gray-200 text-gray-600"}
                        onClick={() => {
                            setEdit1(!edit1);
                            setEdit(false);
                        }}>
                        {status ? t("carDetail.disableMaintenanceMode") : t("carDetail.enableMaintenanceMode")}
                    </Button>
                </>
            )}
        </div>
    );
    return (
        <CarDetailSection
            title={t("maintenance")}
            headerAction={headerAction}
            loading={fetchLoading}
            className="!mt-0"
            embedded
            noPadding>
            {edit && <p className="px-4 text-red-600">{addServiceError}</p>}
            {edit && (
                <table
                    className="fontSemi usertt"
                    style={{
                        textAlign: "left",
                        // width: "100%",
                        padding: "1rem",
                        border: "none",
                        borderBottom: "2px dashed #F2F2FA",
                        overflowY: "scroll",
                    }}>
                    <tbody>
                        <tr>
                            <td>{t("carDetail.serviceType")}</td>
                            <td>
                                <Select
                                    className="w-full"
                                    placeholder={t("carDetail.selectServiceType")}
                                    value={serviceType || undefined}
                                    onChange={setServiceType}
                                    options={serviceTypeOptions.filter((o) => o.value !== "")}
                                />
                            </td>
                            <td>{t("carDetail.vendor")}</td>
                            <td>
                                <input
                                    style={{
                                        backgroundColor: "#eeeeee",
                                        borderRadius: 0,
                                    }}
                                    type="text"
                                    name="vendor"
                                    onChange={addServiceForm.handleChange}
                                    value={addServiceForm.values.vendor}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>{t("carDetail.date")}</td>
                            <td>
                                <input
                                    style={{
                                        backgroundColor: "#eeeeee",
                                        borderRadius: 0,
                                    }}
                                    type="date"
                                    name="date"
                                    onChange={addServiceForm.handleChange}
                                    value={addServiceForm.values.date}
                                    required
                                />
                            </td>
                            <td>{t("carDetail.price")}</td>
                            <td>
                                <input
                                    style={{
                                        backgroundColor: "#eeeeee",
                                        borderRadius: 0,
                                    }}
                                    type="text"
                                    name="price"
                                    onChange={addServiceForm.handleChange}
                                    value={addServiceForm.values.price}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>{t("carDetail.comment")}</td>
                            <td>
                                <input
                                    style={{
                                        backgroundColor: "#eeeeee",
                                        borderRadius: 0,
                                    }}
                                    type="text"
                                    name="comment"
                                    onChange={addServiceForm.handleChange}
                                    value={addServiceForm.values.comment}
                                />
                            </td>
                            <td>{t("carDetail.odometer")}</td>
                            <td>
                                <input
                                    style={{
                                        backgroundColor: "#eeeeee",
                                        borderRadius: 0,
                                    }}
                                    type="text"
                                    name="odometer"
                                    onChange={addServiceForm.handleChange}
                                    value={addServiceForm.values.odometer}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td colSpan="4">
                                <div className="flex items-center gap-3">
                                    <Button
                                        shape="round"
                                        onClick={() => document.getElementById("servicefile")?.click()}>
                                        {t("carDetail.scanCopy")}
                                    </Button>
                                    <input
                                        className="text-sm text-[#acacb9]"
                                        type="file"
                                        id="servicefile"
                                        accept="application/pdf"
                                        onChange={serviceFileChange}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}

            {loading && (
                <div className="flex justify-center p-8">
                    <div>{t("loading")}</div>
                </div>
            )}

            {!edit && !edit1 && !status && !fetchLoading && filteredItems.length === 0 && (
                <div className="p-8">
                    <Empty description={t("carDetail.noMaintenanceHistory")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            )}

            {!edit && !edit1 && !status && filteredItems.map((data, i) => (
                <MaintenanceCard key={`${data.odometer}-${data.doc}-${i}`} data={data} t={t} na={na} showType />
            ))}

            {edit && filteredItems.map((data, i) => (
                <MaintenanceCard key={`edit-${data.odometer}-${data.doc}-${i}`} data={data} t={t} na={na} showType={false} />
            ))}

            {!edit && (
                <>
                    {edit1 && props.status === false ? (
                        <div className="mx-4 mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                            <p className="text-sm leading-relaxed text-gray-700">
                                {t("carDetail.confirmEnableMaintenanceMode")}{" "}
                                <span className="font-semibold text-red-600">{t("carDetail.turnOn")}</span>{" "}
                                {t("carDetail.theMaintenanceMode")}
                            </p>
                            <div className="mt-4 flex flex-wrap gap-3">
                                <Button type="primary" shape="round" className={primaryBtnClass} onClick={turnOn}>
                                    {t("yes")}
                                </Button>
                                <Button shape="round" onClick={() => setEdit1(false)}>
                                    {t("no")}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        status === true && (
                            <div className="mx-4 mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
                                <p className="text-sm text-gray-800">
                                    <span className="font-bold text-red-600">{t("carDetail.maintenanceMode")}</span>{" "}
                                    {t("carDetail.isCurrentlyActivated")}
                                </p>
                                <Button
                                    type="primary"
                                    danger
                                    shape="round"
                                    className="mt-4"
                                    onClick={trunOff}>
                                    {t("carDetail.turnOffMaintenanceMode")}
                                </Button>
                            </div>
                        )
                    )}
                </>
            )}        </CarDetailSection>
    );
}

MaintenCar.propTypes = {
    status: PropTypes.bool,
    data: PropTypes.string,
    id: PropTypes.string,
};

export default MaintenCar;
